/*
 * DHIS2 Metadata browser — platform-admin variant. Displays the synced
 * metadata for a chosen workspace.
 *
 * Performance pattern:
 *   1. On mount + workspace switch, fetch ONLY the summary
 *      (`/api/aggregate-metadata/summary?workspaceId=…`) — ~5KB containing
 *      counts per entity type. Page renders instantly.
 *   2. When the analyst expands a section, fetch THAT section's rows
 *      (`/api/aggregate-metadata/section/<name>?workspaceId=…`). Cached
 *      per (workspace, section) so re-expanding doesn't re-fetch.
 *
 * The previous implementation pulled the full 3 MB snapshot up front
 * over a constrained VM uplink — 30+ seconds even gzipped.
 */

import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { api, ApiError, loadSession } from "../../lib/api.js";
import { useTableState } from "../../lib/useTableState.js";
import TableToolbar from "../../components/TableToolbar.js";
import LivePanel from "../../components/LivePanel.js";

type EntityRow = Record<string, unknown> & { id?: string; name?: string };

type SectionDef = { key: string; label: string; cols: string[] };

const AGGREGATE_SECTIONS: SectionDef[] = [
  { key: "dataSets", label: "Data sets", cols: ["id", "name", "periodType", "categoryComboId"] },
  { key: "dataElements", label: "Data elements", cols: ["id", "name", "valueType", "categoryComboId"] },
  { key: "categoryCombos", label: "Category combos", cols: ["id", "name", "dataDimensionType"] },
  { key: "categories", label: "Categories", cols: ["id", "name", "dataDimensionType"] },
  { key: "categoryOptions", label: "Category options", cols: ["id", "name", "shortName"] },
  { key: "categoryOptionCombos", label: "Category option combos", cols: ["id", "name", "categoryComboId"] },
  { key: "attributeOptionCombos", label: "Attribute option combos", cols: ["id", "name", "attributeComboId"] },
  { key: "indicators", label: "Indicators", cols: ["id", "name", "indicatorTypeId", "shortName"] },
  { key: "indicatorTypes", label: "Indicator types", cols: ["id", "name", "factor", "number"] },
  { key: "indicatorGroups", label: "Indicator groups", cols: ["id", "name"] },
  { key: "dataElementGroups", label: "Data element groups", cols: ["id", "name"] },
  { key: "dataElementGroupSets", label: "Data element group sets", cols: ["id", "name"] },
  { key: "orgUnits", label: "Organisation units", cols: ["id", "name"] },
  { key: "orgUnitGroups", label: "Org unit groups", cols: ["id", "name", "shortName"] },
  { key: "orgUnitGroupSets", label: "Org unit group sets", cols: ["id", "name"] },
  { key: "orgUnitLevels", label: "Org unit levels", cols: ["id", "name", "level"] },
  { key: "optionSets", label: "Option sets", cols: ["id", "name", "valueType"] },
  { key: "options", label: "Options", cols: ["id", "name", "code", "optionSetId"] },
  { key: "attributes", label: "Custom attributes", cols: ["id", "name", "valueType", "appliesTo"] }
];

const TRACKER_SECTIONS: SectionDef[] = [
  { key: "programs", label: "Programs", cols: ["id", "name", "programType", "trackedEntityTypeId"] },
  { key: "programStages", label: "Program stages", cols: ["id", "name", "programId", "repeatable"] },
  { key: "programStageDataElements", label: "Program-stage data elements", cols: ["id", "programStageId", "dataElementId", "compulsory"] },
  { key: "trackedEntityTypes", label: "Tracked entity types", cols: ["id", "name"] },
  { key: "trackedEntityAttributes", label: "Tracked entity attributes", cols: ["id", "name", "valueType", "unique"] },
  { key: "programIndicators", label: "Program indicators", cols: ["id", "name", "programId", "aggregationType"] },
  { key: "programRules", label: "Program rules", cols: ["id", "name", "programId"] },
  { key: "programRuleVariables", label: "Program rule variables", cols: ["id", "name", "programId", "sourceType"] },
  { key: "relationshipTypes", label: "Relationship types", cols: ["id", "name", "fromConstraint", "toConstraint"] }
];

function fmtDate(d?: string): string {
  if (!d) return "—";
  try { return new Date(d).toLocaleString(); } catch { return d; }
}

function pickValue(row: EntityRow, col: string): string {
  const v = row[col];
  if (v == null) return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (typeof v === "object") return JSON.stringify(v).slice(0, 80);
  return String(v);
}

interface SectionViewProps {
  section: SectionDef;
  count: number;
  defaultOpen: boolean;
  loadRows: () => Promise<EntityRow[]>;
}

function SectionView({ section, count, defaultOpen, loadRows }: SectionViewProps) {
  const [rows, setRows] = useState<EntityRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(defaultOpen);
  const t = useTableState((rows ?? []) as Array<Record<string, unknown>>, {
    searchFields: section.cols,
    initialPageSize: 25
  });

  const handleToggle = (e: React.SyntheticEvent<HTMLDetailsElement>) => {
    const willOpen = e.currentTarget.open;
    setOpen(willOpen);
    if (willOpen && rows === null && count > 0) {
      setLoading(true);
      loadRows()
        .then(setRows)
        .catch((err) => setError(err instanceof ApiError ? err.message : String(err)))
        .finally(() => setLoading(false));
    }
  };

  return (
    <details open={open} className="meta-section" onToggle={handleToggle}>
      <summary>
        <span className="meta-section-label">{section.label}</span>
        <span className="pill pill-muted">{count.toLocaleString()}</span>
        {loading ? <span className="pill pill-info">Loading…</span> : null}
        {rows !== null && t.search && t.totalRows !== rows.length ? (
          <span className="pill pill-info">{t.totalRows} matched</span>
        ) : null}
      </summary>
      <div className="meta-section-body">
        {error ? (
          <p className="alert alert-error">{error}</p>
        ) : count === 0 ? (
          <p className="muted">No rows in this snapshot.</p>
        ) : rows === null ? (
          <p className="muted" style={{ padding: 12 }}>
            {loading ? "Loading rows…" : "Click to expand and load."}
          </p>
        ) : (
          <>
            <TableToolbar
              hideExport
              search={t.search}
              setSearch={t.setSearch}
              page={t.page}
              setPage={t.setPage}
              pageSize={t.pageSize}
              setPageSize={t.setPageSize}
              totalPages={t.totalPages}
              totalRows={t.totalRows}
              searchPlaceholder={`Search ${section.label.toLowerCase()}…`}
            />
            <table className="data-table">
              <thead>
                <tr>{section.cols.map((c) => (
                  <th
                    key={c}
                    className="ts-meta-th-sortable"
                    onClick={() => t.toggleSort(c)}
                    title={`Sort by ${c}`}
                  >
                    {c}
                    {t.sortKey === c ? (
                      <span className="ts-meta-sort-arrow">{t.sortDir === "asc" ? " ▲" : " ▼"}</span>
                    ) : null}
                  </th>
                ))}</tr>
              </thead>
              <tbody>
                {(t.paged as unknown as EntityRow[]).map((row, i) => (
                  <tr key={String(row.id ?? i)}>
                    {section.cols.map((c) => <td key={c}>{pickValue(row, c)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </details>
  );
}

export default function AdminDhisMetadata({ mode = "platform" }: { mode?: "platform" | "workspace" }) {
  const [tab, setTab] = useState<"aggregate" | "tracker">("aggregate");
  const session = loadSession();
  const defaultWorkspace = mode === "workspace" ? (session?.workspaceId ?? "__any__") : "__any__";
  const [workspaceId, setWorkspaceId] = useState<string>(defaultWorkspace);
  const [workspaces, setWorkspaces] = useState<string[]>([]);
  const [aggSummary, setAggSummary] = useState<Awaited<ReturnType<typeof api.aggregateMetadataSummary>>["summary"] | null>(null);
  const [trkSummary, setTrkSummary] = useState<Awaited<ReturnType<typeof api.trackerMetadataSummary>>["summary"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState<"aggregate" | "tracker" | null>(null);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  // Load summaries on workspace switch — fast (~5KB).
  useEffect(() => {
    setLoading(true);
    setError(null);
    const wsParam = workspaceId === "__any__" ? undefined : workspaceId;
    Promise.all([
      api.aggregateMetadataSummary(wsParam),
      api.trackerMetadataSummary(wsParam)
    ])
      .then(([a, t]) => {
        setAggSummary(a.summary);
        setTrkSummary(t.summary);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [workspaceId]);

  // Load workspace picker once.
  useEffect(() => {
    if (mode !== "platform") return;
    Promise.all([
      api.aggregateMetadataWorkspaces().catch(() => ({ workspaces: [] as string[] })),
      api.trackerMetadataWorkspaces().catch(() => ({ workspaces: [] as string[] }))
    ]).then(([a, t]) => {
      const merged = Array.from(new Set([...(a.workspaces ?? []), ...(t.workspaces ?? [])])).sort();
      setWorkspaces(merged);
    });
  }, [mode]);

  const currentWorkspaceId = mode === "workspace" && session?.workspaceId ? session.workspaceId : undefined;
  const currentWorkspaceName = mode === "workspace"
    ? (session?.workspaceName ?? session?.organizationName ?? session?.workspaceId ?? "Current workspace")
    : null;

  const triggerSync = async (kind: "aggregate" | "tracker") => {
    setSyncing(kind);
    setSyncMessage(null);
    try {
      const res = kind === "aggregate"
        ? await api.triggerAggregateMetadataSync()
        : await api.triggerTrackerMetadataSync();
      setSyncMessage(`Queued ${kind} metadata sync (${res.job.id}). Refresh after ~10 seconds.`);
    } catch (e) {
      setSyncMessage(e instanceof ApiError ? `Sync failed: ${e.message}` : String(e));
    } finally {
      setSyncing(null);
    }
  };

  const sections = tab === "aggregate" ? AGGREGATE_SECTIONS : TRACKER_SECTIONS;
  const summary = tab === "aggregate" ? aggSummary : trkSummary;
  const wsParam = workspaceId === "__any__" ? undefined : workspaceId;
  const totalRows = useMemo(
    () => sections.reduce((sum, s) => sum + (summary?.counts?.[s.key] ?? 0), 0),
    [sections, summary]
  );

  return (
    <div className="page" style={{ padding: 24 }}>
      <PageBreadcrumbs />
      <header className="page-header">
        <div>
          <h1>DHIS2 metadata</h1>
          <p className="muted">
            Latest snapshot fetched by the aggregate-metadata-sync and
            tracker-metadata-sync workers. Sections load on demand when
            you expand them.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {mode === "platform" ? (
            <>
              <label className="muted" style={{ fontSize: 12 }}>Workspace:</label>
              <select value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)}>
                <option value="__any__">Most recent (any)</option>
                {workspaces.map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </>
          ) : (
            <span className="pill pill-muted">
              Workspace: {currentWorkspaceName}
              {currentWorkspaceId ? ` (${currentWorkspaceId})` : ""}
            </span>
          )}
          <button
            className="btn"
            type="button"
            disabled={syncing !== null}
            onClick={() => triggerSync("aggregate")}
          >
            {syncing === "aggregate" ? "Queuing…" : "Sync aggregate"}
          </button>
          <button
            className="btn"
            type="button"
            disabled={syncing !== null}
            onClick={() => triggerSync("tracker")}
          >
            {syncing === "tracker" ? "Queuing…" : "Sync tracker"}
          </button>
        </div>
      </header>

      {syncMessage ? (
        <div className="notice notice-info" style={{ marginBottom: 12 }}>{syncMessage}</div>
      ) : null}
      {error ? (
        <div className="notice notice-err" style={{ marginBottom: 12 }}>{error}</div>
      ) : null}

      <LivePanel title="Live metadata events" categories={["metadata"]} tailMax={15} />

      <div className="tabs" role="tablist" style={{ marginBottom: 12 }}>
        <button
          role="tab"
          aria-selected={tab === "aggregate"}
          className={tab === "aggregate" ? "tab tab-active" : "tab"}
          onClick={() => setTab("aggregate")}
        >
          Aggregate domain
          {aggSummary ? <span className="pill pill-muted" style={{ marginLeft: 6 }}>v{aggSummary.version ?? "—"}</span> : null}
        </button>
        <button
          role="tab"
          aria-selected={tab === "tracker"}
          className={tab === "tracker" ? "tab tab-active" : "tab"}
          onClick={() => setTab("tracker")}
        >
          Tracker domain
          {trkSummary ? <span className="pill pill-muted" style={{ marginLeft: 6 }}>v{trkSummary.version ?? "—"}</span> : null}
        </button>
      </div>

      <div className="meta-summary" style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <span className="muted">Snapshot: <strong>{summary?.id ?? "—"}</strong></span>
        <span className="muted">Synced: <strong>{fmtDate(summary?.createdAt)}</strong></span>
        <span className="muted">Workspace: <strong>{summary?.workspaceId ?? currentWorkspaceId ?? workspaceId}</strong></span>
        <span className="muted">Total rows: <strong>{totalRows.toLocaleString()}</strong></span>
      </div>

      {loading ? (
        <p className="muted">Loading summary…</p>
      ) : !summary ? (
        <div className="empty-state">
          <p>No {tab} metadata snapshot stored yet for this workspace.</p>
          <p className="muted">
            Click "Sync {tab}" above to fetch the latest from the workspace's DHIS2 instance.
          </p>
        </div>
      ) : (
        <div className="meta-sections">
          {sections.map((section, i) => (
            <SectionView
              key={section.key}
              section={section}
              count={summary.counts?.[section.key] ?? 0}
              defaultOpen={i < 3}
              loadRows={async () => {
                const fn = tab === "aggregate" ? api.aggregateMetadataSection : api.trackerMetadataSection;
                const res = await fn(section.key, wsParam);
                return res.rows as EntityRow[];
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
